import { tWithKeyAttr as t } from "../../i18nHelper";
import React, { useEffect, useRef, useState } from "react";
import styles from "./riskDetailModal.module.css";
import { useUpdateRiskAlertMutation, RiskAlertServerResponse } from '../../services/api/riskAlerts.api';
import Modal from "../modals/modal/Modal";
import ErrorPopup from "../modals/errorPopup/errorPopup";
import { useNavigate } from "react-router-dom";
import PrivacyProtectionPopup from "../modals/privacyProtectionPopup/privacyProtectionPopup";
import { capitalizeKeys } from "../../utils/utils";

interface RiskDetailModalProps {
  alert: RiskAlertServerResponse;
  onClose: () => void;
  onSave: () => void; // Add this new prop
}

const RiskDetailModal: React.FC<RiskDetailModalProps> = ({
  alert,
  onClose,
  onSave,
}) => {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const navigate = useNavigate();

  const [updateRiskAlert] = useUpdateRiskAlertMutation();

  const MAX_LENGTH = 400;

  const [action, setAction] = useState(alert.actionsPerformed ?? "");
  const [recommendation, setRecommendation] = useState(alert.nextSteps ?? "");
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [openPrivacyPopup, setOpenPrivacyPopup] = useState(false);


  const [charCount, setCharCount] = useState({
    action: action.length,
    recommendation: recommendation.length
  });

  const updateCharCount = (field: 'action' | 'recommendation', value: string) => {
    setCharCount(prev => ({ ...prev, [field]: value.length }));
  };

  const isValid = action.length <= MAX_LENGTH && recommendation.length <= MAX_LENGTH;

  const hasRequiredContent = action.trim().length > 0 && recommendation.trim().length > 0;

  const isButtonEnabled = isValid && hasRequiredContent;

  const handleActionChange = (value: string) => {
    setAction(value);
    updateCharCount('action', value);
  };

  const handleRecommendationChange = (value: string) => {
    setRecommendation(value);
    updateCharCount('recommendation', value);
  };

  const getRiskIndicatorClass = (level: string) => {
    switch (level) {
      case "Red":
        return styles.statusIndicatorHigh;
      case "Orange":
        return styles.statusIndicatorMedium;
      case "Green":
        return styles.statusIndicatorLow;
      default:
        return styles.statusIndicatorHigh;
    }
  };

  const getRiskBadgeClass = (level: string) => {
    switch (level) {
      case "Red":
        return styles.riskBadgeHigh;
      case "Orange":
        return styles.riskBadgeMedium;
      case "Green":
        return styles.riskBadgeLow;
      default:
        return styles.riskBadgeHigh;
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleSave = async () => {
    const trimmedAction = action.trim();
    const trimmedRecommendation = recommendation.trim();

    setIsSaving(true);

    try {
      await updateRiskAlert({
        id: alert.id,
        data: {
          actionsPerformed: trimmedAction,
          nextSteps: trimmedRecommendation
        }
      }).unwrap();

      setIsSaving(false);
      setShowSuccess(true);

      timeoutRef.current = setTimeout(() => {
        onSave();
      }, 2000);
    } catch (error) {
      setOpenErrorModal(true);
      setIsSaving(false);
    }
  };
  const handlePrivacyPopupClose = () => {
    // Navigate to EditUserDataPage with user data
    navigate("/users/edit", { state: { user: {Users: capitalizeKeys(alert.user)} } });
    setOpenPrivacyPopup(false);
  };

  const handlePatientClick = () => {
    setOpenPrivacyPopup(true);
  };

  if(openPrivacyPopup) {
    return (
        <PrivacyProtectionPopup
          onClose={handlePrivacyPopupClose}
          clientUsersGuid={alert.user?.guid}
        />
    )}

  if (showSuccess) {
    return (
      <div className={styles.modal}>
        <div className={styles.successState}>
          <div className={styles.successIcon}>✅</div>
          <h3 className={styles.successTitle}>{t('TREATMENT_SUCCESSFUL')}</h3>
          <div className={styles.successAnimation}></div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modal}>
      <div className={`${styles.modalHeader}`}>
        <div className={getRiskIndicatorClass(alert.riskLevel)}></div>
        <h3 className={styles.title}>{alert.riskLabel}</h3>
        {/* <button className={styles.closeButton} onClick={onClose}>
          ×
        </button> */}
      </div>

      <div className={styles.patientInfo}>
        <div className={`${styles.patientAvatar} hover`}
          onClick={() => handlePatientClick()}
        >{(alert.patientFullName || t('NO_NAME_AVAILABLE')).charAt(0)}</div>
        <div className={styles.patientDetails}>
          <p className={styles.name}>
            {alert.patientFullName || t('NO_NAME_AVAILABLE')}{alert.age ? `, ${alert.age}` : ''}
          </p>
          <div className={`${styles.riskBadge} ${getRiskBadgeClass(alert.riskLevel)}`}>
            <span className={styles.riskIcon}>⚠️</span>
            <span className={styles.risk}>{alert.riskLabel}</span>
          </div>
          <p className={styles.identified}>{t('IDENTIFIED_BY')}&nbsp;{alert.sourceName}</p>
        </div>
      </div>

      <div className={styles.formSection}>
        <div className={styles.inputGroup}>
          <label className={styles.label}>
            <span className={styles.labelIcon}>📝</span>
            {t('ACTIONS_COMPLETED')}
            <span className={styles.charCount}>
              {charCount.action}/{MAX_LENGTH}
            </span>
          </label>
          <textarea
            className={`${styles.input} ${charCount.action > MAX_LENGTH ? styles.inputError : ''}`}
            value={action}
            onChange={(e) => handleActionChange(e.target.value)}
            placeholder={t('ACTIONS_TAKEN_TO_MANAGE_RISK')}
            maxLength={MAX_LENGTH}
          />
          {charCount.action > MAX_LENGTH && (
            <span className={styles.errorText}>
              {t('CHARACTER_LIMIT_EXCEEDED')}
            </span>
          )}
        </div>

        <div className={styles.inputGroup}>
          <label className={styles.label}>
            <span className={styles.labelIcon}>💡</span>
            {t('RECOMMENDATIONS_NEXT_STEPS')}
            <span className={styles.charCount}>
              {charCount.recommendation}/{MAX_LENGTH}
            </span>
          </label>
          <textarea
            className={`${styles.input} ${charCount.recommendation > MAX_LENGTH ? styles.inputError : ''}`}
            value={recommendation}
            onChange={(e) => handleRecommendationChange(e.target.value)}
            placeholder={t('ADD_RECOMMENDATIONS_OR_NEXT_STEPS')}
            maxLength={MAX_LENGTH}
          />
          {charCount.recommendation > MAX_LENGTH && (
            <span className={styles.errorText}>
              {t('CHARACTER_LIMIT_EXCEEDED')}
            </span>
          )}
        </div>

        <div className={styles.inputGroup}>
          <label className={`${styles.label} ${styles.labelReadOnly}`}>
            <span className={styles.labelIcon}>🤖</span>
            AI Summary
          </label>
          <textarea
            className={styles.input}
            value={alert.aiSummary || ''}
            readOnly
            placeholder={alert.aiSummary ? undefined : t('NO_AI_SUMMARY_AVAILABLE')}
          />
        </div>
      </div>

      <div className={styles.actions}>
        <button
          className={`${styles.save} ${isSaving || !isButtonEnabled ? styles.saving : ""}`}
          onClick={handleSave}
          disabled={isSaving || !isButtonEnabled}
        >
          {isSaving ? (
            <>
              <div className={styles.spinner}></div>
              <span>{t('SHOMER')}</span>
            </>
          ) : (
            <>
              <span className={styles.buttonIcon}>✓</span>
              <span>{t('SAVE_AND_MARK_AS_DONE')}</span>
            </>
          )}
        </button>
        <button className={styles.cancel} onClick={onClose} disabled={isSaving}>
          {t('CANCEL')}</button>
      </div>
      {openErrorModal && (
        <Modal onClose={() => setOpenErrorModal(false)}>
          <ErrorPopup onClose={() => setOpenErrorModal(false)} />
        </Modal>
      )}
      
    </div>
  );
};

export default RiskDetailModal;
